import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../redux/hooks/reduxHooks'
import {
	selectAccountingPeriods,
	selectAccountingPeriodsError,
	selectAccountingPeriodsStatus,
	selectAccountingPeriodsUpdateStatus,
} from '../../redux/slices/accountingPeriodsSlice'
import { getAccountingPeriods } from '../../redux/actions/accountingPeriodsActions'
import { SelectTypeOption } from '../../types/SelectTypeOptions'
import { FetchStatus, PostStatus } from '../../types/LoadingStates'
import useApi from '../../hooks/useApi'

export type UseCarbonAccountsPeriodResult = {
	accountingPeriodsStatus: FetchStatus
	accountingPeriodsError?: string
	accountingTypeOptions: SelectTypeOption[]
	thisAccountingPeriod: ReturnType<typeof selectAccountingPeriods>[number] | undefined
	companyId: string
	accountingPeriodIdVal: string
	setAccountingPeriod: (option: SelectTypeOption) => void
	updateStatus: PostStatus
}

export function useCarbonAccountsPeriod(): UseCarbonAccountsPeriodResult {
	const api = useApi()
	const dispatch = useAppDispatch()
	const { accountingPeriodId } = useParams()

	const accountingPeriodsStatus = useAppSelector(selectAccountingPeriodsStatus)
	const accountingPeriodsError = useAppSelector(selectAccountingPeriodsError)
	const updateStatus = useAppSelector(selectAccountingPeriodsUpdateStatus)

	const allAccountingPeriodData = useAppSelector(selectAccountingPeriods)
	// A period referenced directly by URL must stay selectable even if it's still mid-upload
	// (e.g. a bookmarked/shared link), so filtering is skipped entirely in that case.
	const filteredAccountingPeriodData = useMemo(() => {
		if (accountingPeriodId !== undefined) {
			return allAccountingPeriodData
		}
		return allAccountingPeriodData.filter(
			(period) => period.status !== 'WAITING_FOR_UPLOAD' && period.upload_state !== null
		)
	}, [allAccountingPeriodData, accountingPeriodId])

	const [companyId, setCompanyId] = useState('')
	const [accountingPeriodIdVal, setAccountingPeriodIdVal] = useState('')

	const [thisAccountingPeriod, setThisAccountingPeriod] = useState(
		filteredAccountingPeriodData[filteredAccountingPeriodData.length - 1]
	)

	const accountingTypeOptions: SelectTypeOption[] = useMemo(() => {
		return filteredAccountingPeriodData.map((period) => ({
			value: period.id,
			label: `${period.start_date} - ${period.end_date}`,
			companyid: period.company_id,
			status: period.status,
		}))
	}, [filteredAccountingPeriodData])

	useEffect(() => {
		if (accountingPeriodsStatus === FetchStatus.initial) {
			dispatch(getAccountingPeriods(api))
		}
	}, [accountingPeriodsStatus, api, dispatch])

	useEffect(() => {
		if (accountingPeriodsStatus !== FetchStatus.loaded) {
			return
		}

		if (accountingPeriodId !== null && accountingPeriodId !== undefined) {
			const matched = filteredAccountingPeriodData.find((period) => period.id === accountingPeriodId)
			if (matched) {
				setThisAccountingPeriod(matched)
				setCompanyId(matched.company_id)
				setAccountingPeriodIdVal(matched.id)
			}
			return
		}

		const latest = filteredAccountingPeriodData[filteredAccountingPeriodData.length - 1]
		if (latest) {
			setThisAccountingPeriod(latest)
			setCompanyId(latest.company_id)
			setAccountingPeriodIdVal(latest.id)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [accountingPeriodsStatus, accountingPeriodId, filteredAccountingPeriodData])

	const setAccountingPeriod = (option: SelectTypeOption) => {
		const matched = filteredAccountingPeriodData.find((period) => period.id === option.value)
		if (matched) {
			setThisAccountingPeriod(matched)
			setCompanyId(matched.company_id)
			setAccountingPeriodIdVal(matched.id)
		}
	}

	return {
		accountingPeriodsStatus,
		accountingPeriodsError,
		accountingTypeOptions,
		thisAccountingPeriod,
		companyId,
		accountingPeriodIdVal,
		setAccountingPeriod,
		updateStatus,
	}
}
